/**
 * "Alertas neste aparelho" — a assinatura de Web Push do navegador.
 *
 * Ligar: pede permissão, assina no serviço de push com a chave pública
 * (VAPID) que a API entrega em /config, e registra na API. Desligar: cancela
 * no navegador e apaga na API. No iOS, push só existe com o app instalado na
 * tela inicial (iOS 16.4+) — a tela explica em vez de falhar em silêncio.
 */

export type EstadoDosAlertas = 'indisponivel' | 'negado' | 'desligado' | 'ligado';

function base64UrlParaBytes(s: string): Uint8Array {
  const padding = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + padding).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export function pushDisponivel(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function assinaturaAtual(): Promise<PushSubscription | null> {
  if (!pushDisponivel()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function estadoDosAlertas(): Promise<EstadoDosAlertas> {
  if (!pushDisponivel()) return 'indisponivel';
  if (Notification.permission === 'denied') return 'negado';
  const sub = await assinaturaAtual();
  if (!sub) return 'desligado';
  const res = await fetch(`/api/v1/push/subscriptions/status?endpoint=${encodeURIComponent(sub.endpoint)}`, { credentials: 'same-origin' });
  const corpo = (await res.json().catch(() => ({ active: false }))) as { active: boolean };
  return corpo.active ? 'ligado' : 'desligado';
}

async function ativaNoServidor(endpoint: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/v1/push/subscriptions/status?endpoint=${encodeURIComponent(endpoint)}`, { credentials: 'same-origin' });
    const corpo = (await res.json()) as { active?: boolean };
    return corpo.active === true;
  } catch {
    return false;
  }
}

export async function ligarAlertas(publicKey: string): Promise<EstadoDosAlertas> {
  if (!pushDisponivel()) return 'indisponivel';
  const permissao = await Notification.requestPermission();
  if (permissao !== 'granted') return permissao === 'denied' ? 'negado' : 'desligado';
  const reg = await navigator.serviceWorker.ready;
  // Uma assinatura que já existe no navegador só é reaproveitada se o
  // servidor a considera ativa (#80, Onda 1): a antiga podia estar morta no
  // serviço de push — o servidor a reativava e o primeiro envio a matava de
  // novo, e a pessoa via "ligado" sem nada chegar. Em dúvida (consulta
  // falhou), nasce uma nova: assinar de novo é barato, ficar surdo não.
  const existente = await reg.pushManager.getSubscription();
  let sub = existente;
  if (existente && !(await ativaNoServidor(existente.endpoint))) {
    await existente.unsubscribe().catch(() => undefined);
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlParaBytes(publicKey) as BufferSource });
  const json = sub.toJSON();
  const res = await fetch('/api/v1/push/subscriptions', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint, keys: json.keys }),
  });
  if (!res.ok) throw new Error(`Não foi possível registrar os alertas (${res.status}).`);
  return 'ligado';
}

export async function desligarAlertas(): Promise<void> {
  const sub = await assinaturaAtual();
  if (!sub) return;
  await fetch('/api/v1/push/subscriptions', {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  }).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

/** iOS exige a PWA instalada para push. */
export function precisaInstalarNoIos(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua);
  const instalado = (navigator as { standalone?: boolean }).standalone === true || matchMedia('(display-mode: standalone)').matches;
  return ios && !instalado;
}
